import { useState, useEffect } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Empty, PageHeader, StatCard } from "@/components/Shell";
import { Modal, Field } from "@/lib/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, HardHat, FileDown, Download, Banknote, ArrowRight } from "lucide-react";

const CATEGORIES = [
  { key: "Cleaning", fr: "Entretien / Ménage" },
  { key: "Security", fr: "Sécurité" },
  { key: "Cook", fr: "Cuisinier / Cantine" },
  { key: "Electrician", fr: "Électricien" },
  { key: "Contractor", fr: "Contractuel" },
  { key: "Chauffeur", fr: "Chauffeur" },
];

const EMPTY = { category: "Cleaning", first_name: "", last_name: "", dob: "", address: "", cell_phone: "", email: "", place_of_birth: "", monthly_salary: "" };

function DocThumb({ label, doc, testid }) {
  const { L } = useLang();
  if (!doc?.path) return null;
  const url = `${API}/files/${doc.path}`;
  const isImg = /\.(png|jpe?g|webp|gif)$/i.test(doc.filename || "");
  const isPdf = (doc.filename || "").toLowerCase().endsWith(".pdf");
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block" data-testid={testid}>
      <div className="text-xs font-medium mb-1">{label}</div>
      <div className="h-28 w-full border border-border rounded-md overflow-hidden bg-secondary flex items-center justify-center hover:ring-2 hover:ring-primary transition">
        {isImg ? <img src={url} alt={label} className="w-full h-full object-cover" />
          : isPdf ? <iframe src={`${url}#toolbar=0&navpanes=0&view=FitH`} title={label} className="w-full h-full pointer-events-none" scrolling="no" />
          : <div className="flex flex-col items-center text-muted-foreground"><Download className="h-6 w-6 mb-1" /><span className="text-[11px]">{L("Open", "Ouvrir")}</span></div>}
      </div>
      <div className="text-[11px] text-muted-foreground truncate mt-1">{doc.filename}</div>
    </a>
  );
}
export default function Personnel({ canManage }) {
  const { L } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [report, setReport] = useState(null);
  const [importingPdf, setImportingPdf] = useState(false);
  const importPdf = async (file) => {
    if (!file) return;
    setImportingPdf(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.raw.post("/extract/person/personnel", fd).then((x) => x.data);
      const g = r.fields || {};
      setF((p) => ({ ...p, first_name: g.first_name || p.first_name, last_name: g.last_name || p.last_name, dob: g.dob || p.dob, address: g.address || p.address, cell_phone: g.cell_phone || p.cell_phone, email: g.email || p.email, place_of_birth: g.place_of_birth || p.place_of_birth }));
      toast.success(L("Form filled from document", "Formulaire rempli depuis le document"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setImportingPdf(false); }
  };
  const incomplete = (p) => !p.first_name || !p.last_name || !p.dob || !p.cell_phone || !p.address || (p.category !== "Contractor" && p.monthly_salary == null);

  const fmtHTG = (n) => (n == null ? "—" : `${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} HTG`);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rep] = await Promise.all([api.get("/personnel"), api.get("/personnel/salary-report")]);
      setRows(r); setReport(rep);
    }
    catch (e) { toast.error(L("Failed to load", "Échec du chargement")); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const catLabel = (k) => { const c = CATEGORIES.find((x) => x.key === k); return c ? L(k, c.fr) : k; };

  const openAdd = () => { setEditId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setF({ ...EMPTY, ...p, monthly_salary: p.monthly_salary ?? "" }); setOpen(true); };

  // Contractor: salary stays empty by default
  const setCategory = (v) => setF((prev) => ({ ...prev, category: v, monthly_salary: v === "Contractor" && !editId ? "" : prev.monthly_salary }));

  const save = async (again = false) => {
    if (!f.first_name || !f.last_name) { toast.error(L("Name required", "Nom requis")); return; }
    const payload = { ...f, monthly_salary: f.monthly_salary === "" || f.monthly_salary === null ? null : Number(f.monthly_salary) };
    try {
      if (editId) { await api.patch(`/personnel/${editId}`, payload); toast.success(L("Personnel updated", "Personnel mis à jour")); }
      else { await api.post("/personnel", payload); toast.success(L("Personnel added", "Personnel ajouté")); }
      setF(EMPTY); setEditId(null); load();
      if (!again) setOpen(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(L(`Remove ${p.first_name} ${p.last_name}?`, `Supprimer ${p.first_name} ${p.last_name} ?`))) return;
    try { await api.del(`/personnel/${p.id}`); toast.success(L("Removed", "Supprimé")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const fiche = async (p) => {
    try {
      const res = await api.raw.get(`/personnel/${p.id}/fiche`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `fiche_${p.first_name}_${p.last_name}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(L("Download failed", "Échec du téléchargement")); }
  };
  const salaryPdf = async () => {
    try {
      const res = await api.raw.get(`/personnel/salary-report/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "rapport_salaires.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(L("Download failed", "Échec du téléchargement")); }
  };
  const uploadDoc = async (p, which, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await api.raw.post(`/personnel/${p.id}/document/${which}`, fd);
      setF((prev) => ({ ...prev, documents: { ...(prev.documents || {}), [which]: { path: res.data.path, filename: res.data.filename } } }));
      toast.success(L("Document uploaded", "Document téléversé")); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;

  const grouped = CATEGORIES.map((c) => ({ ...c, items: rows.filter((r) => r.category === c.key) })).filter((g) => g.items.length > 0);
  const catTotal = (k) => (report?.categories || []).find((c) => c.category === k)?.total ?? 0;

  return (
    <div className="animate-fade-up" data-testid="personnel-page">
      <PageHeader title={L("Other Personnel", "Autre personnel")}
        description={L("Non-teaching staff: cleaning, security, cooks, electricians, contractors, drivers. Salaries in HTG.",
          "Personnel non-enseignant : entretien, sécurité, cuisiniers, électriciens, contractuels, chauffeurs. Salaires en HTG.")}
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={async () => { try { const res = await api.raw.get(`/export/personnel.csv`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "personnel.csv"; a.click(); URL.revokeObjectURL(url); } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Export failed"); } }} data-testid="export-personnel-btn"><Download className="h-4 w-4 mr-1" />{L("Export list", "Exporter la liste")}</Button>
          <Button variant="outline" onClick={async () => { try { const res = await api.raw.get(`/export/personnel.pdf`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "personnel.pdf"; a.click(); URL.revokeObjectURL(url); } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Export failed"); } }} data-testid="export-personnel-pdf-btn"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={salaryPdf} data-testid="salary-report-pdf-btn"><FileDown className="h-4 w-4 mr-1" />{L("Salary report", "Rapport salaires")}</Button>
          {canManage && <Button onClick={openAdd} data-testid="add-personnel-btn"><Plus className="h-4 w-4 mr-1" />{L("Add Personnel", "Ajouter du personnel")}</Button>}
        </div>} />

      {report && rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <StatCard label={L("Head count", "Effectif")} value={report.head_count} icon={HardHat} />
          <StatCard label={L("Monthly payroll (HTG)", "Masse salariale (HTG)")} value={fmtHTG(report.grand_total)} icon={Banknote} tone="primary" />
        </div>
      )}

      {rows.length === 0 ? <Empty title={L("No personnel yet", "Aucun personnel")} /> : grouped.map((g) => (
        <div key={g.key} className="mb-6" data-testid={`personnel-group-${g.key}`}>
          <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2"><HardHat className="h-4 w-4" />{catLabel(g.key)} <span className="text-muted-foreground font-normal">({g.items.length})</span><span className="ml-auto text-xs text-muted-foreground" data-testid={`personnel-cat-total-${g.key}`}>{fmtHTG(catTotal(g.key))}</span></h3>
          <Table>
            <TableHeader><TableRow>
              <TableHead>{L("Name", "Nom")}</TableHead><TableHead>{L("DOB", "Naissance")}</TableHead>
              <TableHead>{L("Phone", "Téléphone")}</TableHead><TableHead>{L("Place of birth", "Lieu de naissance")}</TableHead>
              <TableHead>{L("Salary (HTG)", "Salaire (HTG)")}</TableHead>{canManage && <TableHead className="text-right">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>{g.items.map((p) => (
              <TableRow key={p.id} data-testid={`personnel-row-${p.id}`}>
                <TableCell className="font-medium"><span data-testid={`personnel-name-${p.id}`} className={incomplete(p) ? "text-red-600 font-semibold" : ""} title={incomplete(p) ? L("Missing required info", "Informations manquantes") : undefined}>{p.first_name} {p.last_name}</span>{p.address ? <div className="text-xs text-muted-foreground truncate max-w-[220px]">{p.address}</div> : null}</TableCell>
                <TableCell className="text-sm">{p.dob || "—"}</TableCell>
                <TableCell className="text-sm">{p.cell_phone || "—"}</TableCell>
                <TableCell className="text-sm">{p.place_of_birth || "—"}</TableCell>
                <TableCell className="text-sm">{p.monthly_salary != null ? `${Number(p.monthly_salary).toLocaleString()} HTG` : "—"}</TableCell>
                {canManage && <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => fiche(p)} data-testid={`fiche-personnel-${p.id}`}><FileDown className="h-4 w-4 mr-1" />{L("Sheet", "Fiche")}</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-personnel-${p.id}`}>{L("Edit", "Modifier")}</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(p)} data-testid={`del-personnel-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>}
              </TableRow>))}</TableBody>
          </Table>
        </div>
      ))}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? L("Edit Personnel", "Modifier le personnel") : L("Add Personnel", "Ajouter du personnel")}
        footer={<div className="flex gap-2">
          <Button onClick={() => save(false)} data-testid="save-personnel-btn">{editId ? L("Save", "Enregistrer") : L("Create", "Créer")}</Button>
          {!editId && <Button variant="outline" onClick={() => save(true)} data-testid="save-add-another-personnel-btn"><ArrowRight className="h-4 w-4 mr-1" />{L("Save & add another", "Enregistrer et ajouter")}</Button>}
        </div>}>
        {!editId && <Field label={L("Auto-fill from PDF / photo", "Remplir depuis un PDF / photo")}>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => importPdf(e.target.files?.[0])} data-testid="personnel-pdf-import" className="text-xs" disabled={importingPdf} />
          {importingPdf && <span className="text-xs text-muted-foreground ml-2">{L("Reading…", "Lecture…")}</span>}
        </Field>}
        <Field label={L("Category", "Catégorie")}>
          <Select value={f.category} onValueChange={setCategory}>
            <SelectTrigger data-testid="personnel-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{L(c.key, c.fr)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={L("First name", "Prénom")}><Input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="personnel-first-name" /></Field>
          <Field label={L("Last name", "Nom")}><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="personnel-last-name" /></Field>
          <Field label={L("Date of birth", "Date de naissance")}><Input type="date" value={f.dob || ""} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="personnel-dob" /></Field>
          <Field label={L("Cell phone", "Téléphone")}><Input value={f.cell_phone || ""} onChange={(e) => setF({ ...f, cell_phone: e.target.value })} data-testid="personnel-phone" /></Field>
          <Field label={L("Email", "Courriel")}><Input type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="personnel-email" /></Field>
          <Field label={L("Place of birth", "Lieu de naissance")}><Input value={f.place_of_birth || ""} onChange={(e) => setF({ ...f, place_of_birth: e.target.value })} data-testid="personnel-place-birth" /></Field>
          <Field label={L("Monthly salary (HTG)", "Salaire mensuel (HTG)")}><Input type="number" value={f.monthly_salary} onChange={(e) => setF({ ...f, monthly_salary: e.target.value })} data-testid="personnel-salary" placeholder={f.category === "Contractor" ? L("Enter per contract", "À saisir par contrat") : ""} /></Field>
        </div>
        <Field label={L("Home address", "Adresse du domicile")}>
          <textarea value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} rows={2} data-testid="personnel-address" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </Field>
        {f.category === "Contractor" && <p className="text-xs text-muted-foreground">{L("Contractor pay varies by job — enter the amount manually.", "La rémunération du contractuel varie selon le travail — saisissez le montant manuellement.")}</p>}
        {editId ? (
          <>
            <div className="grid grid-cols-2 gap-3 border-t border-border pt-3 mt-1">
              <Field label={L("Contract (PDF/DOC)", "Contrat (PDF/DOC)")}><input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={(e) => uploadDoc({ id: editId }, "contract", e.target.files?.[0])} data-testid="personnel-contract-upload" className="text-xs" /></Field>
              <Field label={L("ID document (PDF/JPG/PNG)", "Pièce d'identité (PDF/JPG/PNG)")}><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => uploadDoc({ id: editId }, "id_document", e.target.files?.[0])} data-testid="personnel-id-upload" className="text-xs" /></Field>
            </div>
            {(f.documents?.contract || f.documents?.id_document) && (
              <div className="grid grid-cols-2 gap-3 mt-1">
                <DocThumb label={L("Contract", "Contrat")} doc={f.documents?.contract} testid="personnel-thumb-contract" />
                <DocThumb label={L("ID document", "Pièce d'identité")} doc={f.documents?.id_document} testid="personnel-thumb-id" />
              </div>
            )}
          </>
        ) : <p className="text-xs text-muted-foreground">{L("Save first, then re-open to upload the contract & ID.", "Enregistrez d'abord, puis rouvrez pour téléverser le contrat et la pièce d'identité.")}</p>}
      </Modal>
    </div>
  );
}
